import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dumbbell, Activity, TrendingUp } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Analytics } from "@/lib/analytics";
import { GPS_CARDIO_EXERCISE_NAMES, isGpsCardioWorkoutType } from "@/lib/gpsCardioWorkout";
import { formatPaceSecPerKm } from "@/lib/formatPace";
import { fetchAllExerciseRowsForUser, parseCardioNotes } from "@/lib/fetchExerciseHistoryRows";

const MS_PER_DAY = 86400000;

interface ExerciseData {
  exercise_name: string;
  workout_date: string;
  weight_kg: number | null;
  sets: number | null;
  reps: number | null;
  distance_km: number | null;
  duration_seconds: number | null;
  workout_type: string;
}

interface ExerciseProgressChartsProps {
  /** Free plan uses ~30 days; Premium passes a large window for “full history”. */
  historyDays?: number;
}

const isCardioExercise = (name: string, workoutType: string) =>
  isGpsCardioWorkoutType(workoutType) ||
  GPS_CARDIO_EXERCISE_NAMES.some((g) => name.toLowerCase().includes(g.toLowerCase()));

export const ExerciseProgressCharts = ({ historyDays = 30 }: ExerciseProgressChartsProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [exerciseData, setExerciseData] = useState<ExerciseData[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const cutoffMs = useMemo(() => Date.now() - historyDays * MS_PER_DAY, [historyDays]);

  const uniqueExercises = useMemo(() => {
    const names = [...new Set(exerciseData.map((d) => d.exercise_name))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [exerciseData]);

  useEffect(() => {
    Analytics.progressChartViewed('exercise');
    if (user) loadExerciseData();
  }, [user, historyDays]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('exercise-updates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'exercises' },
        () => { loadExerciseData(); },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'workouts' },
        (payload) => {
          if (payload.new && (payload.new as { completed_at?: string }).completed_at) {
            loadExerciseData();
          }
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  useEffect(() => {
    if (uniqueExercises.length === 0) {
      setSelectedExercise("");
      return;
    }
    if (!selectedExercise || !uniqueExercises.includes(selectedExercise)) {
      setSelectedExercise(uniqueExercises[0]);
    }
  }, [uniqueExercises, selectedExercise]);

  const loadExerciseData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Paginate past PostgREST's 1000-row default so recent runs/gym work aren't dropped
      const sinceIso = new Date(Date.now() - Math.max(historyDays, 30) * MS_PER_DAY).toISOString();
      const data = await fetchAllExerciseRowsForUser(user.id, { sinceIso });

      const formattedData: ExerciseData[] = (data || []).map((exercise: any) => {
        const workoutRaw = exercise.workouts;
        const workout = Array.isArray(workoutRaw) ? workoutRaw[0] : workoutRaw;
        const notesMeta = parseCardioNotes(exercise.notes);
        let weightKg = exercise.weight_kg != null ? Number(exercise.weight_kg) : null;
        let sets = exercise.sets != null ? Number(exercise.sets) : null;
        let reps = exercise.reps != null ? Number(exercise.reps) : null;
        if (Array.isArray(exercise.set_details) && exercise.set_details.length > 0) {
          const details = exercise.set_details as Array<{
            weight_kg?: number;
            weight?: number;
            reps?: number;
            completed?: boolean;
          }>;
          const usable = details.filter((s) => s.reps || s.weight_kg || s.weight);
          if (usable.length > 0) {
            sets = usable.length;
            const weights = usable
              .map((s) => Number(s.weight_kg ?? s.weight ?? 0))
              .filter((w) => w > 0);
            if (weights.length > 0) weightKg = Math.max(...weights);
            const repVals = usable.map((s) => Number(s.reps ?? 0)).filter((r) => r > 0);
            if (repVals.length > 0) reps = Math.max(...repVals);
          }
        }
        const distanceKm =
          exercise.distance_km != null
            ? Number(exercise.distance_km)
            : notesMeta.distance != null
              ? Number(notesMeta.distance)
              : null;
        const durationSec =
          exercise.duration_seconds != null
            ? Number(exercise.duration_seconds)
            : notesMeta.durationSeconds != null
              ? Number(notesMeta.durationSeconds)
              : null;
        return {
          exercise_name: exercise.name,
          workout_date: workout?.completed_at || workout?.created_at,
          weight_kg: weightKg,
          sets,
          reps,
          distance_km: distanceKm,
          duration_seconds: durationSec,
          workout_type: workout?.workout_type || 'strength',
        };
      }).filter((d) => !!d.workout_date)
        .sort((a, b) => new Date(a.workout_date).getTime() - new Date(b.workout_date).getTime());

      setExerciseData(formattedData);
    } catch (error) {
      console.error('Error loading exercise data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExerciseProgressData = (exerciseName: string) => {
    return exerciseData
      .filter((d) => d.exercise_name === exerciseName)
      .filter((d) => new Date(d.workout_date).getTime() >= cutoffMs)
      .sort((a, b) => new Date(a.workout_date).getTime() - new Date(b.workout_date).getTime())
      .map((d, index) => {
        const dist = d.distance_km != null ? Number(d.distance_km) : 0;
        const dur = d.duration_seconds != null ? Number(d.duration_seconds) : 0;
        return {
          session: index + 1,
          date: new Date(d.workout_date).toLocaleDateString(),
          sortKey: new Date(d.workout_date).getTime(),
          weight: d.weight_kg != null ? Number(d.weight_kg) : 0,
          sets: d.sets != null ? Number(d.sets) : 0,
          reps: d.reps != null ? Number(d.reps) : 0,
          distance: dist,
          paceSecPerKm: dist > 0 && dur > 0 ? dur / dist : 0,
          workout_type: d.workout_type,
        };
      });
  };

  const selectedExerciseData = selectedExercise ? getExerciseProgressData(selectedExercise) : [];
  const isRunningExercise = selectedExerciseData.length > 0 &&
    (isCardioExercise(selectedExercise, selectedExerciseData[0].workout_type) ||
      selectedExerciseData.every((d) => isGpsCardioWorkoutType(d.workout_type)));

  const [activeTab, setActiveTab] = useState<string>('weight');

  useEffect(() => {
    setActiveTab(isRunningExercise ? 'distance' : 'weight');
  }, [selectedExercise, isRunningExercise]);

  const derivedActiveTab = isRunningExercise
    ? (activeTab === 'distance' || activeTab === 'pace' ? activeTab : 'distance')
    : (activeTab === 'weight' || activeTab === 'sets' || activeTab === 'reps' ? activeTab : 'weight');

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('components.charts.exerciseProgress.loading')}
      </div>
    );
  }

  if (uniqueExercises.length === 0) {
    return (
      <Card className="bg-gradient-card border-0">
        <CardContent className="text-center py-8">
          <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
          <p className="text-muted-foreground">{t('components.charts.exerciseProgress.emptyTitle')}</p>
          <p className="text-sm text-muted-foreground">{t('components.charts.exerciseProgress.emptyHint')}</p>
        </CardContent>
      </Card>
    );
  }

  const ChartTooltip = ({ active, payload, label, formatter }: {
    active?: boolean; payload?: any[]; label?: string;
    formatter?: (value: number) => string;
  }) => {
    if (!active || !payload?.length) return null;
    const value = payload[0]?.value ?? 0;
    const display = formatter ? formatter(value) : String(value);
    return (
      <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
        <p className="font-medium text-sm mb-1">{label}</p>
        <p className="text-sm text-primary">{display}</p>
      </div>
    );
  };

  return (
    <Card className="bg-gradient-card border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-normal">
          <TrendingUp className="h-5 w-5 text-primary" />
          {t('components.charts.exerciseProgress.title')}
        </CardTitle>
        <div className="mt-4">
          <Select value={selectedExercise} onValueChange={setSelectedExercise}>
            <SelectTrigger className="w-full max-w-sm">
              <SelectValue placeholder={t('components.charts.exerciseProgress.selectExercise')} />
            </SelectTrigger>
            <SelectContent>
              {uniqueExercises.map(exercise => (
                <SelectItem key={exercise} value={exercise}>
                  {exercise}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {selectedExerciseData.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>{t('components.charts.exerciseProgress.emptyForExercise')}</p>
          </div>
        ) : isRunningExercise ? (
          <Tabs value={derivedActiveTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="distance" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabDistance')}
              </TabsTrigger>
              <TabsTrigger value="pace" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabPace')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="distance">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip
                      content={({ active, payload, label }) => (
                        <ChartTooltip
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueKm', { value: v.toFixed(1) })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="distance"
                      stroke="hsl(var(--accent))"
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="pace">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      width={44}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => formatPaceSecPerKm(v)}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => (
                        <ChartTooltip
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valuePace', {
                            pace: formatPaceSecPerKm(v),
                          })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="paceSecPerKm"
                      stroke="hsl(var(--gold))"
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <Tabs value={derivedActiveTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="weight" className="flex items-center gap-2">
                <Dumbbell className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabWeight')}
              </TabsTrigger>
              <TabsTrigger value="sets" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabSets')}
              </TabsTrigger>
              <TabsTrigger value="reps" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                {t('components.charts.exerciseProgress.tabReps')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="weight">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip
                      content={({ active, payload, label }) => (
                        <ChartTooltip
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueKg', { value: v })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="weight"
                      stroke="hsl(var(--primary))"
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="sets">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip
                      content={({ active, payload, label }) => (
                        <ChartTooltip
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueSets', { value: v })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Bar dataKey="sets" fill="hsl(var(--gold))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="reps">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={selectedExerciseData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} width={36} tickLine={false} axisLine={false} />
                    <Tooltip
                      content={({ active, payload, label }) => (
                        <ChartTooltip
                          active={active} payload={payload} label={label}
                          formatter={(v) => t('components.charts.exerciseProgress.valueReps', { value: v })}
                        />
                      )}
                      contentStyle={{ padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}
                    />
                    <Bar dataKey="reps" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};
